declare var ol: any;

export class ParentPolygonManager {
  private parentPolygonFeature: any = null;
  private parentPolygonSource: any = null;
  private parentPolygonLayer: any = null;
  private parentPolygonCoords: number[][] = [];
  private tolerancePixels: number = 1; // Tolérance d'un pixel pour les points sur la bordure

  constructor(
    private getMap: () => any,
    private parentPolygonColor: string
  ) {}

  get feature(): any {
    return this.parentPolygonFeature;
  }

  get hasParent(): boolean {
    return this.parentPolygonFeature !== null;
  }

  drawParentPolygon(polygonJson: string, parsePolygonJson: (json: string) => [number, number][] | null): void {
    const map = this.getMap();
    if (!polygonJson || !map) return;

    // Remove existing parent polygon layer
    if (this.parentPolygonLayer) {
      map.removeLayer(this.parentPolygonLayer);
    }

    const polygonCoordinates = parsePolygonJson(polygonJson);
    if (!polygonCoordinates || polygonCoordinates.length < 3) return;

    try {
      const firstCoord = polygonCoordinates[0];
      const isProjected = Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90;

      const transformedRing = isProjected
        ? polygonCoordinates
        : polygonCoordinates.map(coord => ol.proj.fromLonLat(coord));

      // Sauvegarder les coordonnées transformées pour les tests de point-in-polygon
      this.parentPolygonCoords = transformedRing;

      const polygonGeometry = new ol.geom.Polygon([transformedRing]);

      this.parentPolygonFeature = new ol.Feature({
        geometry: polygonGeometry,
        type: 'parentPolygon'
      });

      this.parentPolygonFeature.setStyle(new ol.style.Style({
        stroke: new ol.style.Stroke({
          color: '#ff0000',
          width: 3,
          lineDash: [10, 5]
        }),
        fill: new ol.style.Fill({ color: this.parentPolygonColor })
      }));

      this.parentPolygonSource = new ol.source.Vector();
      this.parentPolygonSource.addFeature(this.parentPolygonFeature);

      this.parentPolygonLayer = new ol.layer.Vector({
        source: this.parentPolygonSource,
        zIndex: 0
      });

      map.addLayer(this.parentPolygonLayer);

      // Fit view to parent polygon
      const extent = polygonGeometry.getExtent();
      if (extent && !extent.some((val: any) => !isFinite(val))) {
        map.getView().fit(extent, {
          padding: [50, 50, 50, 50],
          maxZoom: 17
        });
      }
    } catch (error) {
      console.error('Error drawing parent polygon:', error);
    }
  }

  /** Remplissage transparent : seules les bordures (stroke) restent visibles. */
  setParentFillHidden(hidden: boolean): void {
    if (!this.parentPolygonFeature) {
      return;
    }
    const fillColor = hidden ? 'rgba(0,0,0,0)' : this.parentPolygonColor;
    this.parentPolygonFeature.setStyle(
      new ol.style.Style({
        stroke: new ol.style.Stroke({
          color: '#ff0000',
          width: 3,
          lineDash: [10, 5],
        }),
        fill: new ol.style.Fill({ color: fillColor }),
      })
    );
    this.parentPolygonSource?.changed?.();
  }

  /**
   * Vérifie si un point est à l'intérieur du polygone parent
   * Utilise l'algorithme ray-casting qui fonctionne mieux avec les polygones concaves
   * Inclut une tolérance pour les points sur la bordure
   */
  isPointInsideParentPolygon(coordinate: number[]): boolean {
    if (!this.parentPolygonFeature) return true;

    const parentGeometry = this.parentPolygonFeature.getGeometry();

    // Vérifier avec OpenLayers
    if (parentGeometry.intersectsCoordinate(coordinate)) {
      return true;
    }

    // Vérifier avec ray-casting (pour les polygones concaves)
    if (this.pointInPolygonRayCasting(coordinate, this.parentPolygonCoords)) {
      return true;
    }

    // Vérifier si le point est sur ou très proche de la bordure
    // Cela permet de modifier des polygones qui ont les mêmes bordures que le parent
    if (this.isPointOnOrNearEdge(coordinate)) {
      return true;
    }

    return false;
  }

  /**
   * Algorithme ray-casting pour vérifier si un point est dans un polygone
   * Fonctionne mieux avec les polygones concaves
   */
  private pointInPolygonRayCasting(point: number[], polygon: number[][]): boolean {
    if (!polygon || polygon.length < 3) return false;

    const x = point[0];
    const y = point[1];
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0], yi = polygon[i][1];
      const xj = polygon[j][0], yj = polygon[j][1];

      const intersect = ((yi > y) !== (yj > y)) &&
        (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

      if (intersect) {
        inside = !inside;
      }
    }

    return inside;
  }

  /**
   * Vérifie si un point est sur ou très proche d'un bord du polygone parent
   * Utilise une tolérance basée sur la résolution de la carte
   */
  private isPointOnOrNearEdge(coordinate: number[]): boolean {
    if (!this.parentPolygonCoords || this.parentPolygonCoords.length < 3) return false;

    // Calculer la tolérance basée sur la résolution de la carte
    const map = this.getMap();
    let tolerance = 1; // Valeur par défaut en unités de carte

    if (map) {
      const resolution = map.getView().getResolution();
      if (resolution) {
        tolerance = this.tolerancePixels * resolution;
      }
    }

    // Vérifier la distance à chaque segment du polygone
    for (let i = 0; i < this.parentPolygonCoords.length - 1; i++) {
      const p1 = this.parentPolygonCoords[i];
      const p2 = this.parentPolygonCoords[i + 1];
      const distance = this.pointToLineDistance(coordinate, p1, p2);
      if (distance <= tolerance) {
        return true;
      }
    }

    // Vérifier aussi le dernier segment (fermeture du polygone)
    const lastIdx = this.parentPolygonCoords.length - 1;
    if (lastIdx > 0) {
      const distance = this.pointToLineDistance(
        coordinate,
        this.parentPolygonCoords[lastIdx],
        this.parentPolygonCoords[0]
      );
      if (distance <= tolerance) {
        return true;
      }
    }

    return false;
  }

  /**
   * Calcule la distance d'un point à un segment de ligne
   */
  private pointToLineDistance(point: number[], lineStart: number[], lineEnd: number[]): number {
    const A = point[0] - lineStart[0];
    const B = point[1] - lineStart[1];
    const C = lineEnd[0] - lineStart[0];
    const D = lineEnd[1] - lineStart[1];

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;

    if (lenSq !== 0) {
      param = dot / lenSq;
    }

    let xx, yy;

    if (param < 0) {
      xx = lineStart[0];
      yy = lineStart[1];
    } else if (param > 1) {
      xx = lineEnd[0];
      yy = lineEnd[1];
    } else {
      xx = lineStart[0] + param * C;
      yy = lineStart[1] + param * D;
    }

    const dx = point[0] - xx;
    const dy = point[1] - yy;

    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Vérifie si un polygone fils est valide par rapport au parent
   * Permet les polygones qui sont à l'intérieur OU sur la bordure du parent
   */
  isPolygonInsideParentPolygon(polygonGeometry: any): boolean {
    if (!this.parentPolygonFeature) return true;

    const childCoords = polygonGeometry.getCoordinates()[0];

    // Vérifier que tous les sommets sont à l'intérieur ou sur la bordure
    for (const coord of childCoords) {
      if (!this.isPointInsideParentPolygon(coord)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Vérifie si le polygone fils est strictement plus petit que le parent
   * (au moins un point est à l'intérieur, pas seulement sur la bordure)
   */
  isPolygonSmallerThanParent(polygonGeometry: any): boolean {
    if (!this.parentPolygonFeature) return true;

    const childCoords = polygonGeometry.getCoordinates()[0];
    const parentGeometry = this.parentPolygonFeature.getGeometry();

    // Au moins un point doit être strictement à l'intérieur
    for (const coord of childCoords) {
      if (parentGeometry.intersectsCoordinate(coord) && !this.isPointOnOrNearEdge(coord)) {
        return true;
      }
      if (this.pointInPolygonRayCasting(coord, this.parentPolygonCoords) && !this.isPointOnOrNearEdge(coord)) {
        return true;
      }
    }

    // Vérifier le centroïde du polygone fils
    const extent = polygonGeometry.getExtent();
    const centroid = [
      (extent[0] + extent[2]) / 2,
      (extent[1] + extent[3]) / 2
    ];

    return parentGeometry.intersectsCoordinate(centroid) ||
           this.pointInPolygonRayCasting(centroid, this.parentPolygonCoords);
  }

  clipCoordinateToParent(coordinate: number[]): number[] {
    if (!this.parentPolygonFeature) return coordinate;

    if (this.isPointInsideParentPolygon(coordinate)) {
      return coordinate;
    }

    const parentGeometry = this.parentPolygonFeature.getGeometry();
    return parentGeometry.getClosestPoint(coordinate);
  }

  getParentBoundaryIntersection(insidePoint: number[], outsidePoint: number[]): number[] {
    if (!this.parentPolygonFeature) return outsidePoint;

    const parentGeometry = this.parentPolygonFeature.getGeometry();
    const parentCoords = parentGeometry.getCoordinates()[0];

    let closestIntersection: number[] | null = null;
    let minDistance = Infinity;

    for (let i = 0; i < parentCoords.length - 1; i++) {
      const edgeStart = parentCoords[i];
      const edgeEnd = parentCoords[i + 1];

      const intersection = this.getLineIntersection(insidePoint, outsidePoint, edgeStart, edgeEnd);

      if (intersection) {
        const dx = intersection.point[0] - insidePoint[0];
        const dy = intersection.point[1] - insidePoint[1];
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < minDistance) {
          minDistance = distance;
          closestIntersection = intersection.point;
        }
      }
    }

    return closestIntersection || this.clipCoordinateToParent(outsidePoint);
  }

  private getLineIntersection(
    p1: number[], p2: number[],
    p3: number[], p4: number[]
  ): { point: number[], t: number } | null {
    const x1 = p1[0], y1 = p1[1];
    const x2 = p2[0], y2 = p2[1];
    const x3 = p3[0], y3 = p3[1];
    const x4 = p4[0], y4 = p4[1];

    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

    if (Math.abs(denom) < 0.000001) {
      return null;
    }

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return {
        point: [
          x1 + t * (x2 - x1),
          y1 + t * (y2 - y1)
        ],
        t: t
      };
    }

    return null;
  }

  clearParentPolygon(): void {
    const map = this.getMap();
    if (this.parentPolygonLayer && map) {
      map.removeLayer(this.parentPolygonLayer);
      this.parentPolygonLayer = null;
      this.parentPolygonFeature = null;
      this.parentPolygonSource = null;
      this.parentPolygonCoords = [];
    }
  }

  getParentPolygonGeometry(): any {
    return this.parentPolygonFeature?.getGeometry() || null;
  }
}
